import { apiFetch } from './http';
import type { LoginAnswer } from './mfa';

/** A membership as `auth.list_memberships` returns it (ISO-01b.3). */
export interface Membership {
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  userId: string;
  status: string;
  /**
   * Whether the tenant has a logo of its own, and its version for cache-busting: the tenant
   * selector runs before any tenant is active, so the memberships carry what
   * `/tenant-settings/appearance` would say. Absent in a mock: the mark then tries the image.
   */
  hasLogo?: boolean;
  logoVersion?: string | null;
}

/**
 * `userId` is null during a support session (ISO-25.3, revisión del manual, ronda 2, punto 3): it
 * entered the tenant through a grant, with no `users` row behind it, so there is no tenant user to
 * name.
 */
export interface ActiveTenant {
  tenantId: string;
  userId: string | null;
  tenantName: string;
  tenantSlug: string;
  /**
   * IANA zone of the tenant (`tenants.time_zone`): the one every date and hour of the console is
   * shown in and "today" is taken from. Optional only so that a mock written before it existed
   * still types; the API always sends it.
   */
  timeZone?: string;
  /**
   * The day the tenant's week starts on, 0 (Sunday) to 6 (Saturday), from "Primer día de la
   * semana" of Marca y datos (`AM-361`): the calendars of the console and the portal and every
   * "esta semana" start on it. Optional for the same reason as `timeZone`; without it, Monday.
   */
  firstDayOfWeek?: number;
}

/** Payload of `POST /auth/login`, `GET /auth/session` and `POST /auth/session/tenant`. */
export interface SessionState {
  status: string;
  identity: { id: string; email: string; isPlatformAdmin: boolean };
  activeTenant: ActiveTenant | null;
  memberships: Membership[];
  permissions: string[];
  /** ISO-24, `AM-134` point 11: always present, mirroring `Me.platform` below. */
  platform: PlatformIdentitySummary;
  expiresAt: string;
}

export interface RoleSummary {
  id: string;
  code: string;
  name: string;
  isSystem: boolean;
}

/**
 * What the identity behind the session is on the platform side of the boundary (ISO-24, `AM-134`
 * point 7): the mark of `identities.is_platform_admin` and, when a live row of `platform_admins`
 * backs it, the keys of 08 §6.2 it holds.
 */
export interface PlatformIdentitySummary {
  isPlatformAdmin: boolean;
  permissions: string[];
}

/**
 * The grant the current request is running under (ISO-25.3): present only inside a support
 * session, and what the console keeps its persistent band from (13 §3.4).
 */
export interface SupportAccessSummary {
  grantId: string;
  tenantId: string;
  tenantName: string;
  scope: string;
  expiresAt: string;
}

/**
 * Payload of `GET /me` (`AM-134` point 7): the user and
 * person behind the active membership, its roles and effective permissions, plus the two blocks
 * of the boundary.
 *
 * The user half is `null` inside a support session (ISO-25.3): it entered through a grant, not a
 * membership, so there is no `users` row behind it. Every field still arrives explicitly `null`
 * rather than absent (07 §1.15), so a client tells "no user behind this session" apart from "the
 * field was not sent" - consumers tolerate `null` and absent alike, never `!`.
 */
export interface Me {
  tenantId: string;
  userId?: string | null;
  userStatus?: string | null;
  personId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  roles: RoleSummary[];
  permissions: string[];
  platform: PlatformIdentitySummary;
  supportAccess?: SupportAccessSummary | null;
  /** `AM-213`: the person's own organization, absent the same way `personId` is inside a support session. */
  organizationId?: string | null;
}

/** The session, or (`AM-198`) the second-factor challenge that stands before it: see `isMfaChallenge`. */
export function login(input: {
  email: string;
  password: string;
  rememberMe: boolean;
}): Promise<LoginAnswer> {
  return apiFetch<LoginAnswer>('/api/v1/auth/login', { method: 'POST', body: input });
}

export function logout(): Promise<void> {
  return apiFetch<void>('/api/v1/auth/logout', { method: 'POST' });
}

export function getSession(signal?: AbortSignal): Promise<SessionState> {
  return apiFetch<SessionState>('/api/v1/auth/session', signal === undefined ? {} : { signal });
}

export function selectTenant(tenantId: string): Promise<SessionState> {
  return apiFetch<SessionState>('/api/v1/auth/session/tenant', {
    method: 'POST',
    body: { tenantId },
  });
}

export function getMe(signal?: AbortSignal): Promise<Me> {
  return apiFetch<Me>('/api/v1/me', signal === undefined ? {} : { signal });
}

/** `POST /auth/password/forgot` answers 202 whether or not the email exists (ISO-28b). */
export function requestPasswordReset(email: string): Promise<void> {
  return apiFetch<void>('/api/v1/auth/password/forgot', { method: 'POST', body: { email } });
}

export function resetPassword(input: { token: string; newPassword: string }): Promise<void> {
  return apiFetch<void>('/api/v1/auth/password/reset', { method: 'POST', body: input });
}

/** `POST /auth/invitations/accept` opens the session in the inviting tenant (08 §2.2.2). */
export function acceptInvitation(input: {
  token: string;
  password: string;
}): Promise<SessionState> {
  return apiFetch<SessionState>('/api/v1/auth/invitations/accept', { method: 'POST', body: input });
}

/** `usable`, `already_used`, or `invalid` (expired, replaced by a newer invitation, or not ours). */
export type InvitationState = 'usable' | 'already_used' | 'invalid';

/** `POST /auth/invitations/status` (`AM-178`): what the link is worth before the page shows its form. */
export async function invitationStatus(token: string): Promise<InvitationState> {
  const response = await apiFetch<{ state: InvitationState }>('/api/v1/auth/invitations/status', {
    method: 'POST',
    body: { token },
  });
  return response.state;
}

/** `usable`, `already_used` (this link set the password), or `invalid` (expired, replaced by a newer one, or not ours). */
export type PasswordResetState = 'usable' | 'already_used' | 'invalid';

/** `POST /auth/password/reset/status` (`AM-223`): what the reset link is worth before the page shows its form. */
export async function passwordResetStatus(token: string): Promise<PasswordResetState> {
  const response = await apiFetch<{ state: PasswordResetState }>(
    '/api/v1/auth/password/reset/status',
    {
      method: 'POST',
      body: { token },
    },
  );
  return response.state;
}

/**
 * `POST /auth/support-sessions` (ISO-25.3, `AM-134` point 7): the platform administrator enters
 * the tenant of a grant that is theirs and still usable. Answers the grant's own summary, not a
 * session state - the caller reloads the session before it navigates (`AM-134` point 7).
 */
export function openSupportSession(grantId: string): Promise<SupportAccessSummary> {
  return apiFetch<SupportAccessSummary>('/api/v1/auth/support-sessions', {
    method: 'POST',
    body: { grantId },
  });
}

/** `POST /auth/support-sessions/close` (ISO-25.3): leaves the tenant, the grant stays alive. */
export function closeSupportSession(): Promise<void> {
  return apiFetch<void>('/api/v1/auth/support-sessions/close', { method: 'POST' });
}
